import { Stack, useRouter } from 'expo-router';
import React from 'react';
import {
  Alert,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import BackButton from '@/components/back-button';
import ContactSupportModal from '@/components/contact-support-modal';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Error } from '@/ui/icons/error';
import { Hourglass } from '@/ui/icons/hourglass';
import { PendingCheckmarkBubble } from '@/ui/icons/pending-checkmark-bubble';

import Footer from '../footer';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

interface VerificationScreenProps {
  name: string;
  status: 'uploaded' | 'successful' | 'rejected';
  onGoBack: () => void;
  errorMsg?: string;
}

export default function VerificationScreen({
  name,
  status,
  onGoBack,
  errorMsg,
}: VerificationScreenProps) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const [contactSupportModalVisible, setContactSupportModalVisible] =
    React.useState(false);

  const details = React.useMemo(() => {
    if (status === 'rejected') {
      return {
        headerTitle: 'Verification Failed',
        circle: (
          <Icon
            type="naked"
            icon={<Error />}
            fillColor={theme.colors.error}
            width={40}
            height={40}
          />
        ),
        title: 'Oops! Something Went Wrong.',
        body:
          errorMsg ||
          `We couldn’t verify your ${name}. Please check your uploads and try the process again.`,
        onGoBack: onGoBack,
      };
    }

    if (status === 'uploaded') {
      return {
        headerTitle: 'Verification In Progress',
        circle: (
          <Icon
            type="naked"
            icon={<Hourglass />}
            fillColor={theme.colors.pending}
            width={40}
            height={40}
          />
        ),
        title: 'Upload Is Complete!',
        body: `Your ${name} has been uploaded successfully and is under review. We’ll notify you once verification is complete.`,
        onGoBack: onGoBack,
      };
    }

    if (status === 'successful') {
      return {
        headerTitle: 'Verification Successful',
        circle: (
          <Icon
            type="naked"
            icon={<PendingCheckmarkBubble />}
            fillColor={theme.colors.success}
            width={40}
            height={40}
          />
        ),
        title: "You're All Set!",
        body: `Your ${name} has been successfully verified.`,
        onGoBack: onGoBack,
      };
    }
  }, [status, name, onGoBack]);

  if (!details) return null;

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        backgroundColor: theme.colors.background_1,
        alignItems: 'center',
      }}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS === 'web' &&
      ['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: details.headerTitle,
            headerTitle: details.headerTitle,
            headerLeft: () => <BackButton onPress={onGoBack} />,
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
          }}
        />
      )}

      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />

            <Text type="largeTitle">Verify your {name}</Text>
          </View>
        ) : null}

        <View
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16,
            flexGrow: 1,
          }}
        >
          <View>{details.circle}</View>

          <View style={{ gap: 16, alignItems: 'center' }}>
            <Text type="title2">{details.title}</Text>

            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              {details.body}
            </Text>
          </View>
        </View>

        <View
          style={{
            alignItems: 'center',
            gap: 16,
            justifyContent: 'flex-end',
          }}
        >
          {['uploaded', 'rejected'].includes(status) ? (
            <View style={{ flexDirection: 'row' }}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Need help?{` `}
              </Text>

              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web') {
                    setContactSupportModalVisible(true);
                    return;
                  }

                  Alert.alert('', 'Need help? Contact support.', [
                    {
                      text: 'Cancel',
                      onPress: () => {},
                      style: 'cancel',
                    },
                    {
                      text: '+1 855 598 7090',
                      onPress: () => {
                        Linking.openURL('tel:+18555987090');
                      },
                    },
                    {
                      text: 'support@eonrides.com',
                      onPress: () => {
                        Linking.openURL('mailto:support@eonrides.com');
                      },
                    },
                  ]);
                }}
              >
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Contact Support.
                </Text>
              </Pressable>
            </View>
          ) : null}

          <Button text="Go Back" type="ghost" onPress={details.onGoBack} />
        </View>
      </View>

      <ContactSupportModal
        isVisible={contactSupportModalVisible}
        onDismiss={() => setContactSupportModalVisible(false)}
      />

      <Footer />
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_1,
    padding: 16,
    gap: 16,
    justifyContent: 'space-between',
    width: {
      md: 600,
    },
    height: {
      md: 450,
    },
  },
}));
